import { create } from "zustand"
import { persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"

import { clearProfileCache } from "@/http/auth/get-profile"
import { clearSessionCache } from "@/http/auth/session"
import { listOrganizations } from "@/http/organizations"
import type { TOrganizationWithRole } from "@/http/organizations/types"
import {
  defineOrganizationAbilityFor,
  type TOrganizationAbility,
} from "@/lib/auth/organization-abilities"
import { logger } from "@/lib/logger"

export type TOrganizationState = {
  // Organization state
  organizations: TOrganizationWithRole[]
  currentOrg: TOrganizationWithRole | null
  isLoading: boolean
  error: string | null

  // Permissions for current organization
  ability: TOrganizationAbility | null
  can: TOrganizationAbility["can"] | null
  cannot: TOrganizationAbility["cannot"] | null

  // Actions
  loadOrganizations: () => Promise<void>
  setCurrentOrg: (org: TOrganizationWithRole | null) => void
  clearCurrentOrg: () => void
  reset: () => void
}

const initialState = {
  organizations: [],
  currentOrg: null,
  isLoading: false,
  error: null,
  ability: null,
  can: null,
  cannot: null,
}

export const useOrganization = create(
  persist(
    immer<TOrganizationState>((set, get) => ({
      ...initialState,

      /**
       * Load organizations from API with auto-connection logic:
       * - If 1 organization: auto-connect to it
       * - If 0 organizations: user will be redirected to onboarding (Phase 3)
       * - If 2+ organizations: user will be redirected to selection (Phase 2)
       */
      loadOrganizations: async () => {
        set((state) => {
          state.isLoading = true
          state.error = null
        })

        logger.info("Loading organizations")

        const { data, error } = await listOrganizations()

        if (error) {
          logger.error("Failed to load organizations", error)
          set((state) => {
            state.isLoading = false
            state.error = error.message
          })
          return
        }

        logger.info("Organizations loaded", {
          count: data.organizations.length,
        })

        set((state) => {
          state.organizations = data.organizations
          state.isLoading = false
          state.error = null
        })

        // Auto-connection logic
        if (data.organizations.length === 1) {
          logger.info("Auto-connecting to single organization", {
            slug: data.organizations[0].slug,
          })
          get().setCurrentOrg(data.organizations[0])
        } else if (data.organizations.length === 0) {
          logger.info("No organizations found - user needs onboarding")
        } else {
          logger.info(
            "Multiple organizations found - user needs to select one",
            {
              count: data.organizations.length,
            },
          )
        }
      },

      /**
       * Set the current organization and update abilities
       */
      setCurrentOrg: (org: TOrganizationWithRole | null) => {
        if (!org) {
          get().clearCurrentOrg()
          return
        }

        logger.info("Setting current organization", {
          slug: org.slug,
          role: org.role,
        })

        const ability = defineOrganizationAbilityFor(org.role)

        // Clear caches on org switch to ensure fresh preloads
        clearProfileCache()
        clearSessionCache()

        set((state) => {
          state.currentOrg = org
          state.ability = ability
          state.can = ability.can
          state.cannot = ability.cannot
        })
      },

      /**
       * Clear the current organization
       */
      clearCurrentOrg: () => {
        logger.info("Clearing current organization")

        // Clear caches on org clear
        clearProfileCache()
        clearSessionCache()

        set((state) => {
          state.currentOrg = null
          state.ability = null
          state.can = null
          state.cannot = null
        })
      },

      /**
       * Reset the entire store to initial state
       */
      reset: () => {
        logger.info("Resetting organization store")
        // Clear caches on org store reset
        clearProfileCache()
        clearSessionCache()
        set(initialState)
      },
    })),
    {
      name: "organization-storage",
      partialize: (state) => ({
        // Only persist these fields
        currentOrg: state.currentOrg,
      }),
    },
  ),
)
